'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'next/navigation';
import Header from '@/components/common/Header';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import Image from 'next/image';
import { blogService, Blog } from '@/services/blogService';

interface PdfBlock {
  title: string;
  url: string;
}

export default function BlogDetailPage() {
  const params = useParams();
  const slug = params?.slug as string;
  const [blog, setBlog] = useState<Blog | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const loadBlog = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await blogService.getBlogBySlug(slug);
      if (error || !data) {
        setNotFound(true);
      } else {
        setBlog(data);
      }
    } catch (err) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    if (slug) loadBlog();
  }, [slug, loadBlog]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Split content around PDF blocks to render them inline — handles &quot; escaped attributes
  const splitContentWithPdfBlocks = (content: string): Array<{ type: 'html' | 'pdf'; html?: string; pdf?: PdfBlock }> => {
    if (!content) return [];
    const decoded = content.replace(/&quot;/g, '"');
    const parts: Array<{ type: 'html' | 'pdf'; html?: string; pdf?: PdfBlock }> = [];
    const pdfBlockRegex = /<div[^>]*data-pdf-block[^>]*>[\s\S]*?<\/div>/gi;
    let lastIndex = 0;
    let match;
    while ((match = pdfBlockRegex.exec(decoded)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'html', html: decoded.slice(lastIndex, match.index) });
      }
      const tag = match[0];
      const urlMatch = tag.match(/data-pdf-url="([^"]*)"/);
      const titleMatch = tag.match(/data-pdf-title="([^"]*)"/);
      const url = urlMatch ? urlMatch[1] : '';
      const title = titleMatch ? titleMatch[1] : 'Download PDF';
      parts.push({ type: 'pdf', pdf: { url, title } });
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < decoded.length) {
      parts.push({ type: 'html', html: decoded.slice(lastIndex) });
    }
    if (parts.length === 0) {
      parts.push({ type: 'html', html: decoded });
    }
    return parts;
  };

  const contentParts = blog ? splitContentWithPdfBlocks(blog.content) : [];

  const handlePdfDownload = (url: string) => {
    // Silently increment download count in background
    if (blog?.id) {
      blogService.incrementPdfDownloadCount(blog.id).catch(() => {
        // Silent fail — tracking should never block the download
      });
    }
    // Trigger download
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
  };

  return (
    <>
      <Header />
      <main className="pt-16 lg:pt-20 min-h-screen bg-background">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
          </div>
        ) : notFound || !blog ? (
          <div className="text-center py-24">
            <Icon name="DocumentTextIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h1 className="text-2xl font-headline font-bold text-foreground mb-4">Blog not found</h1>
            <Link href="/blogs" className="text-primary hover:underline font-body">
              ← Back to all blogs
            </Link>
          </div>
        ) : (
          <>
            {/* Hero */}
            {blog.featured_image && (
              <div className="relative h-64 md:h-96 w-full overflow-hidden">
                <img
                  src={blog.featured_image}
                  alt={blog.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              </div>
            )}

            <article className="max-w-3xl mx-auto px-4 py-12">
              {/* Back link */}
              <Link
                href="/blogs"
                className="inline-flex items-center space-x-2 text-muted-foreground hover:text-primary transition-colors font-body mb-8"
              >
                <Icon name="ArrowLeftIcon" size={16} />
                <span>Back to Blogs</span>
              </Link>

              {/* Tags */}
              {(blog.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {(blog.tags || []).map((tag, i) => (
                    <span key={i} className="px-3 py-1 bg-primary/10 text-primary text-sm font-body rounded-full">{tag}</span>
                  ))}
                </div>
              )}

              {/* Title */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-headline font-bold text-foreground mb-6 leading-tight">
                {blog.title}
              </h1>

              {/* Meta */}
              <div className="flex items-center gap-4 text-muted-foreground font-body text-sm mb-8 pb-8 border-b border-border">
                {blog.published_date && (
                  <span className="flex items-center gap-1">
                    <Icon name="CalendarIcon" size={16} />
                    {formatDate(blog.published_date)}
                  </span>
                )}
                {blog.read_time && (
                  <span className="flex items-center gap-1">
                    <Icon name="ClockIcon" size={16} />
                    {blog.read_time} min read
                  </span>
                )}
              </div>

              {/* Excerpt */}
              {blog.excerpt && (
                <p className="text-lg text-muted-foreground font-body mb-8 italic border-l-4 border-primary pl-4">
                  {blog.excerpt}
                </p>
              )}

              {/* Content with inline PDF blocks */}
              <div className="font-body text-foreground">
                {contentParts.map((part, idx) =>
                  part.type === 'html' ? (
                    <div
                      key={idx}
                      className="prose prose-lg max-w-none font-body text-foreground prose-headings:font-headline prose-headings:text-foreground prose-p:text-foreground prose-p:leading-relaxed prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground prose-ul:text-foreground prose-ol:text-foreground prose-li:text-foreground prose-blockquote:text-muted-foreground prose-blockquote:border-primary"
                      dangerouslySetInnerHTML={{ __html: part.html || '' }}
                    />
                  ) : part.pdf ? (
                    <div key={idx} className="my-6 p-5 rounded-2xl border border-border bg-card flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon name="DocumentArrowDownIcon" size={22} className="text-primary" />
                        </div>
                        <div>
                          <p className="font-headline font-semibold text-foreground text-sm">{part.pdf.title}</p>
                          <p className="text-muted-foreground text-xs font-body mt-0.5">Click to download this PDF resource</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handlePdfDownload(part.pdf!.url)}
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl font-body font-semibold text-sm hover:bg-primary/90 active:bg-primary/80 transition-colors flex-shrink-0"
                      >
                        <Icon name="ArrowDownTrayIcon" size={16} />
                        Download PDF
                      </button>
                    </div>
                  ) : null
                )}
              </div>
            </article>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="py-16 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-8">
          <Link href="/homepage" className="flex items-center gap-3">
            <div className="relative w-8 h-8 rounded-lg overflow-hidden">
              <Image src="/assets/images/C_-_GoPMM_2_-_Square-1770356625482.png" alt="GoPMM Logo" fill className="object-contain" />
            </div>
            <span className="font-display font-bold text-xl">GoPMM</span>
          </Link>
          <div className="flex flex-wrap justify-center gap-8 text-sm font-semibold text-slate-500">
            <a className="hover:text-[#FF6B4A] transition-colors" href="https://www.linkedin.com/company/gopmm/">LinkedIn</a>
            <a className="hover:text-[#FF6B4A] transition-colors" href="mailto:varun@gopmm.in">Contact Us</a>
          </div>
          <p className="text-sm text-slate-500">© 2024 GoPMM Community. Built for India.</p>
        </div>
      </footer>
    </>
  );
}
